import { Component, HostListener, OnInit } from '@angular/core';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import {
  AG_GRID_PAGE_SIZE_OPTIONS,
  AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_DEFAULT_ROW_HEIGHT_PX,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
  syncInfiniteRowModelPageSize,
} from '../shared/ag-grid/ag-grid-pagination.util';
import {
  createUtcDateFilterParams,
  formatUtcDateTimeCell,
  mapAgGridFilterToApi,
  withDateColumnLayout,
} from '../shared/ag-grid/ag-grid-date-column.util';

import { get } from 'lodash'; // for copy data cell value
import { CommonModule } from '@angular/common';
import { ButtonCellRendererComponent } from '../shared/components/button-cell-renderer/button-cell-renderer.component';
import { DeviceService } from '../services/device-service/device.service';
import { AgGridCopyCellTooltipDirective } from '../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';

@Component({
  selector: 'app-device-config-history',
  standalone: true,
  imports: [AgGridAngular, CommonModule, ButtonCellRendererComponent, AgGridModule, AgGridCopyCellTooltipDirective],
  templateUrl: './device-config-history.component.html',
  styleUrl: './device-config-history.component.scss'
})
export class DeviceConfigHistoryComponent implements OnInit {
  /** AG Grid colId → device-config-audits search API sort field. */
  private static readonly SORT_FIELD_MAP: Record<string, string> = {
    dateTime: 'dateTime',
    firmwareVersion: 'firmwareVersion',
    responseFirmwareVersion: 'responseFirmwareVersion',
    configVersion: 'configVersion',
    response: 'response',
    comment: 'comment',
    gatewayId: 'gatewayId',
    customerName: 'customerName',
    groupName: 'groupName',
    id: 'id',
  };

  rowData: any;
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  gridApi: any;
  page: number = 0;
  totalElements: any;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
  sortFlag: boolean = false;
  deviceId: any;
  currentPage: any;
  storedSorting: any;
  sortArray: any[] = [];
  gatewayId: any;
  constructor(private deviceService: DeviceService) {}
  ngOnInit(): void {
    // sorting
    this.storedSorting = sessionStorage.getItem('deviceConfigHistorySortingState');
    if (this.storedSorting) {
      const deviceConfigHistorySortingState = JSON.parse(this.storedSorting);
      this.deviceConfigSearchPayload.sorts[0].field = this.resolveSortField(
        deviceConfigHistorySortingState.colId
      );
      this.deviceConfigSearchPayload.sorts[0].direction =
        deviceConfigHistorySortingState.sort;
    }
  }

  defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    resizable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  /** Simple text floating filter — same pattern as Configuration History. */
  private readonly textFilterParams = {
    filterOptions: ['contains'],
    defaultOption: 'contains',
    trimInput: true,
    maxNumConditions: 1,
    suppressFilterButton: true,
  };

  private readonly dateColumnFilterParams = createUtcDateFilterParams();

  columnDefs: ColDef[] = [
    withDateColumnLayout({
      headerName: 'Check In Time (UTC)',
      field: 'dateTime',
      sort: 'desc',
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      filterParams: this.dateColumnFilterParams,
      cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.dateTime),
    }),
    {
      headerName: 'Requested Firmware Version',
      field: 'firmwareVersion',
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      valueGetter: (params) => {
        if (params.data?.firmwareVersion == undefined) {
          return 'N/A';
        } else if (params.data?.firmwareVersion == null || params.data?.firmwareVersion == '') {
          return 'N/A';
        } else {
          return params.data?.firmwareVersion;
        }
      },
    },
    {
      headerName: 'Responded Firmware Version',
      field: 'responseFirmwareVersion',
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      valueGetter: (params) => {
        if (params.data?.responseFirmwareVersion == undefined) {
          return 'N/A';
        } else if (
          params.data?.responseFirmwareVersion == null ||
          params.data?.responseFirmwareVersion == ''
        ) {
          return 'N/A';
        } else {
          return params.data?.responseFirmwareVersion;
        }
      },
    },
    {
      headerName: 'Config Version',
      field: 'configVersion',
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      valueGetter: (params) => {
        if (params.data?.configVersion == undefined) {
          return 'N/A';
        } else if (params.data?.configVersion == null || params.data?.configVersion == '') {
          return 'N/A';
        } else {
          return params.data?.configVersion;
        }
      },
    },
    {
      headerName: 'Result',
      field: 'response',
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.response?.toString() ? params?.data?.response?.toString() : 'N/A';
      },
    },
    {
      headerName: 'Comment',
      field: 'comment',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      valueGetter: (params) => {
        if (params.data?.comment == undefined) {
          return 'N/A';
        } else if (params.data?.comment == null || params.data?.comment == '') {
          return 'N/A';
        } else {
          return params.data?.comment;
        }
      },
    },
  ];

  /* Start Copy Data cell Value function   */
  contextMenu: { visible: boolean; x: number; y: number; value: any } = {
    visible: false,
    x: 0,
    y: 0,
    value: '',
  };
  selectedCellValue: string = '';
  contextMenuVisible: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  getNestedProperty(obj: any, path: string): any {
    return get(obj, path, 'N/A'); // 'N/A' if value is undefined
  }

  onCellContextMenu(event: any) {
    event.event.preventDefault(); // Prevent the browser's default context menu

    let cellValue;
    const columnField = event.colDef.field;

    // If the column has a valueGetter, call it manually
    if (event.colDef.valueGetter) {
      cellValue = event.colDef.valueGetter({ data: event.data });
    } else {
      // Use lodash's get method for nested properties
      cellValue = this.getNestedProperty(event.data, columnField);
    }

    // Set context menu visibility and position
    this.contextMenu = {
      visible: true,
      x: event.event.clientX,
      y: event.event.clientY,
      value: cellValue || 'N/A',
    };
  }

  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value).then(() => {
      this.contextMenu.visible = false; // Hide context menu after copying
    }).catch((err) => {
      console.error('Failed to copy:', err);
    });
  }

  // Hide context menu on outside click
  @HostListener('document:click')
  onDocumentClick(): void {
    this.contextMenu.visible = false;
  }
  preventDefaultContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }
  /* End Copy Data cell Value function   */

  deviceConfigSearchPayload = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    filters: [
      {
        field: 'device.id',
        operator: 'eq',
        value: '',
      },
    ],
    sorts: [
      {
        field: 'dateTime',
        direction: 'desc',
      },
    ],
  };

  onPaginationChanged(event: PaginationChangedEvent): void {
    syncInfiniteRowModelPageSize(
      event,
      this.paginationPageSize,
      (newPageSize) => {
        this.paginationPageSize = newPageSize;
        this.deviceConfigSearchPayload.size = newPageSize;
        this.deviceConfigSearchPayload.page = 0;
      }
    );
    this.currentPage = event.api.paginationGetCurrentPage();
    this.deviceConfigSearchPayload.page = this.currentPage;
  }

  sortForAgGrid(sortModel: any) {
    if (
      sortModel[0] == undefined ||
      sortModel[0] == null ||
      sortModel[0].colId == undefined ||
      sortModel[0].colId == null
    ) {
      return;
    }
    this.sortFlag = true;
    sessionStorage.setItem(
      'deviceConfigHistorySortingState',
      JSON.stringify(sortModel[0])
    );
    this.deviceConfigSearchPayload.sorts[0].field = this.resolveSortField(
      sortModel[0].colId
    );
    this.deviceConfigSearchPayload.sorts[0].direction = sortModel[0].sort;
  }

  /**
   * Map AG Grid floating filters → API filters (same as Configuration History),
   * always keeping the current-device gatewayId scope filter.
   */
  filterForAgGrid(filterModel: any) {
    const newFilters: any[] = [];

    // Scope audits to the device open on Device Details (existing behavior).
    if (this.gatewayId) {
      newFilters.push({
        field: 'gatewayId',
        operator: 'like',
        value: this.gatewayId,
      });
    } else if (this.deviceId) {
      newFilters.push({
        field: 'device.id',
        operator: 'eq',
        value: this.deviceId,
      });
    }

    if (filterModel && Object.keys(filterModel).length > 0) {
      Object.keys(filterModel).forEach((key) => {
        const filter = filterModel[key];
        if (!filter) {
          return;
        }
        const mapped = mapAgGridFilterToApi(key, filter);
        if (mapped) {
          newFilters.push(mapped);
        }
      });
    }

    this.deviceConfigSearchPayload.filters =
      newFilters.length > 0 ? newFilters : [{ field: '', operator: '', value: '' }];
  }

  private resolveSortField(colId: string): string {
    return DeviceConfigHistoryComponent.SORT_FIELD_MAP[colId] ?? colId;
  }

  onGridReady(params: GridReadyEvent) {
    this.deviceId = localStorage.getItem('deviceId');
    this.gatewayId = localStorage.getItem('gatewayIdDetails');
    this.gridApi = params;

    const storedSorting = sessionStorage.getItem('deviceConfigHistorySortingState');

    if (storedSorting) {
      const parsedSort = JSON.parse(storedSorting);

      // Apply sort state on grid
      this.gridApi.api.applyColumnState({
        state: [{ colId: parsedSort.colId, sort: parsedSort.sort }],
        applyOrder: false,
      });
    }
    const dataSource = {
      getRows: (params: any) => {
        this.sortForAgGrid(params.sortModel);
        this.filterForAgGrid(params.filterModel);

        const pageSize = this.paginationPageSize || AG_GRID_DEFAULT_PAGE_SIZE;
        this.deviceConfigSearchPayload.size = pageSize;
        this.deviceConfigSearchPayload.page = Math.floor(params.startRow / pageSize);

        this.deviceService.getDeviceConfigAudits(this.deviceConfigSearchPayload).subscribe({
          next: (data: any) => {
            if (data.data) {
              this.rowData = data?.data?.content;
              this.totalElements = data?.data?.totalElements;
              this.syncGridHostHeight(
                Array.isArray(this.rowData) ? this.rowData.length : 0
              );
              params.successCallback(this.rowData, this.totalElements);
            } else {
              this.syncGridHostHeight(0);
              params.successCallback([], 0);
            }
          },
          error: (error) => {
            console.error('API Error:', error);
            this.syncGridHostHeight(0);
            params.failCallback();
          },
        });
      },
    };
    this.gridApi.api.setGridOption('datasource', dataSource);
  }

  /**
   * Size host to the loaded page of rows (default fixed row height).
   * Full content height so the grid does not need an internal vertical scrollbar.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    const bodyPx =
      rows > 0 ? rows * AG_GRID_DEFAULT_ROW_HEIGHT_PX : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx;
    const minHostPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
  }
}
